import React, { useEffect, useState, useRef } from "react";
import { Input, Card, Form } from "antd";
import Swal from "sweetalert2";
import {
  getPrint,
  updatePrint,
  getPrintOrdersByOfferAndPrinter,
} from "../../core/api/products";
import { getMediaPlansByOfferAndPrinter } from "../../core/api/mediaplan";
const PrintDetailsView = ({ printId, clientId }) => {
  const [form] = Form.useForm();
  const [pricingHistory, setPricingHistory] = useState([]);
  const [print, setPrint] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const printDetails = (await getPrint(printId)).data;
        setPrint(printDetails);
        const stockRemaining =
          printDetails.qty_printed - printDetails.total_qty_used;
        const unitPrice =
          printDetails.qty_printed > 0
            ? (
                printDetails.total_value_print / printDetails.qty_printed
              )?.toFixed(3)
            : 0;

        form.setFieldsValue({
          product: printDetails.product_name,
          productVariation: printDetails.product_variation_desc,
          offer: printDetails.code_offer,

          category: printDetails.type_of_offer,
          qtyPrinted: printDetails.qty_printed,
          totalValuePrint: printDetails.total_value_print,
          totalQtyUsed: printDetails.total_qty_used,
          stockRemaining,
          stockRemainingValue: (stockRemaining * unitPrice)?.toFixed(3),
          unitPrice,
          actualStockRemaining:
            printDetails.actual_stock_remaining || stockRemaining,
          actualValueStockRemaining: (
            printDetails.actual_stock_remaining * unitPrice
          )?.toFixed(3),
        });

        const orderHistoryData = await getPrintOrdersByOfferAndPrinter(
          printDetails.offer,
          printDetails.printer
        );
        setPricingHistory(orderHistoryData.data || []);
      } catch (error) {
        console.error(error);
      }
    };

    if (clientId && printId) fetchData();
  }, [clientId, printId]);

  const handleSubmit = async () => {
    try {
      const values = form.getFieldsValue();
      await updatePrint(printId, {
        actual_stock_remaining: values.actualStockRemaining,
      });

      Swal.fire("Success", "Print stock updated successfully!", "success");
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to update print stock.", "error");
    }
  };

  const [printUsageHistory, setPrintUsageHistory] = useState([]);

  useEffect(() => {
    const fetchPrintUsageHistory = async () => {
      try {
        const response = await getMediaPlansByOfferAndPrinter(
          print?.offer,
          print?.printer
        );
        setPrintUsageHistory(response.data || []);
      } catch (error) {
        console.error("Error fetching print usage history", error);
        Swal.fire("Error", "Failed to fetch usage history.", "error");
      }
    };

    if (print) fetchPrintUsageHistory();
  }, [print]);

  return (
    <div>
      <div>
        <Form form={form} layout="vertical">
          <br />
          <Card title="Print Stock Details">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
              }}
            >
              <Form.Item label="QTY Printed" name="qtyPrinted">
                <Input disabled />
              </Form.Item>
              <Form.Item label="Total Value Print" name="totalValuePrint">
                <Input disabled />
              </Form.Item>
              <Form.Item label="Total Qty Used" name="totalQtyUsed">
                <Input disabled />
              </Form.Item>
              <Form.Item label="Unit Price" name="unitPrice">
                <Input disabled />
              </Form.Item>
              <Form.Item label="Stock Remaining (QTY)" name="stockRemaining">
                <Input disabled />
              </Form.Item>

              <Form.Item
                label="Stock Remaining (value)"
                name="stockRemainingValue"
              >
                <Input disabled />
              </Form.Item>

              <Form.Item
                label="Actual Stock Remaining (QTY)"
                name="actualStockRemaining"
                rules={[
                  {
                    required: true,
                    message: "Please enter actual stock remaining.",
                  },
                ]}
              >
                <Input
                  type="number"
                  onChange={(e) => {
                    const unitPrice =
                      parseFloat(form.getFieldValue("unitPrice")) || 0;
                    const actualStockRemaining =
                      parseFloat(e.target.value) || 0;
                    form.setFieldsValue({
                      actualValueStockRemaining: (
                        actualStockRemaining * unitPrice
                      ).toFixed(3),
                    });
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Actual Stock Remaining Value"
                name="actualValueStockRemaining"
              >
                <Input disabled />
              </Form.Item>
            </div>
          </Card>
          <br />
          {pricingHistory.length > 0 && (
            <Card title="Print Pricing History">
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Invoice Number</th>
                      <th>Printer</th>
                      <th>Quantity</th>
                      <th>Total Price</th>
                      <th>Unit Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingHistory.map((history, index) => (
                      <tr key={index}>
                        <td>{history.invoice_date}</td>
                        <td>{history.invoice_number}</td>
                        <td>{history.printer_name}</td>
                        <td>{history.quantity}</td>
                        <td>${history.total_cost?.toFixed(3)}</td>
                        <td>
                          ${(history.total_cost / history.quantity)?.toFixed(3)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          <br />
          {print && (
            <Card title="Print Usage History">
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Campaign Code</th>
                      <th>Quantity</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {printUsageHistory.length > 0 ? (
                      printUsageHistory
                        .reduce((acc, row, index) => {
                          const previousBalance =
                            index === 0
                              ? form.getFieldValue("actualStockRemaining") -
                                row.target_mail_qty
                              : acc[index - 1].balance - row.target_mail_qty;

                          const balance =
                            previousBalance < 0 ? 0 : previousBalance;

                          acc.push({
                            ...row,
                            balance: previousBalance,
                            className: previousBalance < 0 ? "bg-danger" : "",
                          });

                          return acc;
                        }, [])
                        .map((row, index) => (
                          <tr key={index} className={row.className}>
                            <td>{row.date}</td>
                            <td>{row.campaign_code}</td>
                            <td>{row.target_mail_qty}</td>
                            <td
                              className={
                                row.balance < 0 ? "bg-danger text-white" : ""
                              }
                            >
                              {row.balance}
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center">
                          No usage history available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          <br />
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-submit"
              onClick={handleSubmit}
            >
              Update Print Stock
            </button>
          </div>
          <br />
        </Form>
      </div>
    </div>
  );
};

export default PrintDetailsView;
